import { useEffect, useState } from 'react';
import {
  CategoryError,
  buildTree,
  childrenOf,
  findNearDuplicates,
  type Category,
  type CategoryKind,
} from '../../core/categories';
import {
  createCategory,
  createTag,
  mergeCategories,
  renameCategory,
  setCategoryArchived,
  watchCategories,
  watchTags,
  type Tag,
} from '../../data/categories';
import type { UserRecord } from '../../data/auth';

interface Props {
  record: UserRecord;
}

export function CategoriesScreen({ record }: Props) {
  const [categories, setCategories] = useState<Category[] | null>(null);
  const [tags, setTags] = useState<Tag[]>([]);
  const [kind, setKind] = useState<CategoryKind>('expense');
  const [showArchived, setShowArchived] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => watchCategories(setCategories), []);
  useEffect(() => watchTags(setTags), []);

  async function run(action: () => Promise<unknown>) {
    setError(null);
    try {
      await action();
    } catch (cause) {
      setError(
        cause instanceof CategoryError || cause instanceof Error
          ? cause.message
          : 'That did not work.',
      );
    }
  }

  if (categories === null) return <p className="muted">Loading categories…</p>;

  const tree = buildTree(categories, kind, { includeArchived: showArchived });
  const duplicates = findNearDuplicates(categories.filter((c) => !c.archived));

  return (
    <>
      <h1>Categories</h1>

      {/* FR-CAT-03: one screen for both, filtered by type. */}
      <div className="basis-toggle" role="group" aria-label="Category kind">
        <button type="button" aria-pressed={kind === 'expense'} onClick={() => setKind('expense')}>
          Expense categories
        </button>
        <button type="button" aria-pressed={kind === 'income'} onClick={() => setKind('income')}>
          Income heads
        </button>
      </div>

      {error && <p className="error">{error}</p>}
      {notice && <p className="muted" aria-live="polite">{notice}</p>}

      {categories.length === 0 && (
        <div className="placeholder">
          <p>No categories yet — and none are seeded on purpose.</p>
          <p className="small">
            The list is drafted from your own 637 imported rows and handed to you for
            editing before import runs. Inventing one here would put categories in your
            books that your spending never used. You can still add any you want now.
          </p>
        </div>
      )}

      {duplicates.length > 0 && (
        <section className="panel">
          <h2>Possible duplicates</h2>
          <p className="muted small">
            Similar names, which usually means the same thing typed twice. Nothing merges
            on its own — each is your call.
          </p>
          <ul className="notes">
            {duplicates.map((group) => (
              <li key={group.map((c) => c.id).join('|')}>
                {group.map((c) => c.name).join('  ·  ')}
              </li>
            ))}
          </ul>
        </section>
      )}

      <NewCategory
        kind={kind}
        categories={categories}
        onCreate={(input) => run(() => createCategory(input, categories, record.uid))}
      />

      {tree.map((node) => (
        <section key={node.category.id} className="panel">
          <div className="head-row">
            <h2>{node.category.name}</h2>
            {node.category.id !== '__orphans__' && (
              <CategoryActions
                category={node.category}
                categories={categories}
                onRename={(name) => run(() => renameCategory(node.category, name, record.uid))}
                onArchive={() =>
                  run(() => setCategoryArchived(node.category, !node.category.archived, record.uid))
                }
                onMerge={(targetId) =>
                  run(async () => {
                    const moved = await mergeCategories(
                      node.category.id, targetId, categories, record.uid,
                    );
                    setNotice(
                      `Merged. ${moved} transactions re-pointed.` +
                        (moved > 0 ? ' Rollups need recomputing for the affected months.' : ''),
                    );
                  })
                }
              />
            )}
          </div>

          {node.children.length === 0 ? (
            <p className="muted tiny">No subcategories.</p>
          ) : (
            <table className="table compact">
              <tbody>
                {node.children.map((child) => (
                  <tr key={child.id} className={child.archived ? 'archived' : undefined}>
                    <td>
                      {child.name}
                      {child.archived && <span className="badge">archived</span>}
                    </td>
                    <td className="actions">
                      <CategoryActions
                        category={child}
                        categories={categories}
                        onRename={(name) => run(() => renameCategory(child, name, record.uid))}
                        onArchive={() =>
                          run(() => setCategoryArchived(child, !child.archived, record.uid))
                        }
                        onMerge={(targetId) =>
                          run(async () => {
                            const moved = await mergeCategories(
                              child.id, targetId, categories, record.uid,
                            );
                            setNotice(`Merged. ${moved} transactions re-pointed.`);
                          })
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      ))}

      <p className="muted">
        <button type="button" className="btn-link" onClick={() => setShowArchived((v) => !v)}>
          {showArchived ? 'Hide' : 'Show'} archived
        </button>
        {' · Categories archive, never delete — history keeps its labels.'}
      </p>

      <TagPanel tags={tags} onCreate={(name) => run(() => createTag(name, tags, record.uid))} />
    </>
  );
}

function NewCategory({
  kind,
  categories,
  onCreate,
}: {
  kind: CategoryKind;
  categories: Category[];
  onCreate: (input: { name: string; kind: CategoryKind; parentId: string | null }) => void;
}) {
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState<string>('');

  const parents = categories.filter((c) => c.kind === kind && c.parentId === null && !c.archived);

  return (
    <section className="panel">
      <h2>Add {kind === 'income' ? 'income head' : 'category'}</h2>
      <div className="row">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={kind === 'income' ? 'e.g. Consulting' : 'e.g. Groceries'}
          aria-label="Name"
        />
        <select value={parentId} onChange={(e) => setParentId(e.target.value)} aria-label="Parent">
          <option value="">Top level</option>
          {parents.map((p) => (
            <option key={p.id} value={p.id}>
              under {p.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn btn-primary"
          disabled={name.trim() === ''}
          onClick={() => {
            onCreate({ name, kind, parentId: parentId || null });
            setName('');
          }}
        >
          Add
        </button>
      </div>
    </section>
  );
}

function CategoryActions({
  category,
  categories,
  onRename,
  onArchive,
  onMerge,
}: {
  category: Category;
  categories: Category[];
  onRename: (name: string) => void;
  onArchive: () => void;
  onMerge: (targetId: string) => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const [merging, setMerging] = useState(false);
  const [draft, setDraft] = useState(category.name);

  const orphanCount = childrenOf(categories, category.id).length;
  const targets = categories.filter((c) => c.id !== category.id && c.kind === category.kind && !c.archived);

  if (renaming) {
    return (
      <span className="row">
        <input value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus aria-label="New name" />
        <button
          type="button"
          className="btn-link"
          onClick={() => {
            onRename(draft);
            setRenaming(false);
          }}
        >
          Save
        </button>
        <button type="button" className="btn-link" onClick={() => setRenaming(false)}>
          Cancel
        </button>
      </span>
    );
  }

  if (merging) {
    return (
      <span className="row">
        <select
          defaultValue=""
          aria-label="Merge into"
          onChange={(e) => {
            if (e.target.value) onMerge(e.target.value);
            setMerging(false);
          }}
        >
          <option value="">Merge into…</option>
          {targets.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        <button type="button" className="btn-link" onClick={() => setMerging(false)}>
          Cancel
        </button>
      </span>
    );
  }

  return (
    <span>
      <button type="button" className="btn-link" onClick={() => setRenaming(true)}>
        Rename
      </button>
      <button type="button" className="btn-link" onClick={() => setMerging(true)}>
        Merge
      </button>
      <button
        type="button"
        className="btn-link"
        onClick={onArchive}
        title={
          orphanCount > 0 && !category.archived
            ? `${orphanCount} subcategories would be left without a visible parent`
            : undefined
        }
      >
        {category.archived ? 'Restore' : 'Archive'}
      </button>
    </span>
  );
}

function TagPanel({ tags, onCreate }: { tags: Tag[]; onCreate: (name: string) => void }) {
  const [name, setName] = useState('');

  return (
    <section className="panel">
      <h2>Tags</h2>
      <p className="muted small">
        Free-form and multiple per transaction, for things that cut across categories and
        months — a flat renovation, say.
      </p>
      {tags.length > 0 && (
        <ul className="chips">
          {tags.map((tag) => (
            <li key={tag.id} className="chip">
              {tag.name}
              {tag.usageCount > 0 && ` · ${tag.usageCount}`}
            </li>
          ))}
        </ul>
      )}
      <div className="row">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. flat-renovation"
          aria-label="Tag name"
        />
        <button
          type="button"
          className="btn"
          disabled={name.trim() === ''}
          onClick={() => {
            onCreate(name);
            setName('');
          }}
        >
          Add tag
        </button>
      </div>
    </section>
  );
}
